import type { LocaleCode } from "../../../packages/foundation/src/index.js";

export type ConnectionRequestRow = {
  id: string;
  locale: string;
  requestedRole: string;
  status: string;
  uid: string;
};

export type ConnectionRequestSummary = {
  accepted: number;
  needsReview: number;
  pending: number;
  total: number;
};

export type ConnectionRequestFilter = keyof ConnectionRequestSummary;

type RequestStatusGroup = "accepted" | "needsReview" | "pending";

const connectionRequestFilters: ConnectionRequestFilter[] = [
  "total",
  "pending",
  "accepted",
  "needsReview",
];

const statusLabels: Record<LocaleCode, Record<string, string>> = {
  en: {
    accepted: "Accepted",
    accepted_existing_connection: "Already connected",
    needs_review: "Needs review",
    pending_auto_accept: "Pending auto-accept",
    rejected: "Rejected",
  },
  ar: {
    accepted: "تم القبول",
    accepted_existing_connection: "متصل مسبقاً",
    needs_review: "بحاجة إلى مراجعة",
    pending_auto_accept: "بانتظار القبول التلقائي",
    rejected: "مرفوض",
  },
};

export const summaryLabels: Record<LocaleCode, Record<keyof ConnectionRequestSummary, string>> = {
  en: {
    accepted: "Accepted",
    needsReview: "Needs review",
    pending: "Pending",
    total: "Total",
  },
  ar: {
    accepted: "مقبولة",
    needsReview: "للمراجعة",
    pending: "معلقة",
    total: "الإجمالي",
  },
};

export const noConnectionRequestsMessage: Record<LocaleCode, string> = {
  en: "No welcome requests for this organization yet.",
  ar: "لا توجد طلبات ترحيب لهذه المؤسسة بعد.",
};

export const noPendingConnectionRequestsMessage: Record<LocaleCode, string> = {
  en: "No pending connection requests for this organization.",
  ar: "لا توجد طلبات اتصال معلقة لهذه المؤسسة.",
};

export const noConnectionRequestsForFilterMessage: Record<
  LocaleCode,
  Record<ConnectionRequestFilter, string>
> = {
  en: {
    accepted: "No accepted connection requests for this organization.",
    needsReview: "No connection requests need review.",
    pending: noPendingConnectionRequestsMessage.en,
    total: noConnectionRequestsMessage.en,
  },
  ar: {
    accepted: "لا توجد طلبات اتصال مقبولة لهذه المؤسسة.",
    needsReview: "لا توجد طلبات اتصال بحاجة إلى مراجعة.",
    pending: noPendingConnectionRequestsMessage.ar,
    total: noConnectionRequestsMessage.ar,
  },
};

export function summarizeConnectionRequests(
  requests: Pick<ConnectionRequestRow, "status">[],
): ConnectionRequestSummary {
  const summary: ConnectionRequestSummary = {
    accepted: 0,
    needsReview: 0,
    pending: 0,
    total: requests.length,
  };

  for (const request of requests) {
    summary[statusGroup(request.status)] += 1;
  }

  return summary;
}

export function requestStatusLabel(status: string, locale: LocaleCode): string {
  return statusLabels[locale][status] ?? status.replaceAll("_", " ");
}

export function requestStatusClass(status: string): string {
  const group = statusGroup(status);

  return group === "needsReview" ? "status-needs-review" : `status-${group}`;
}

export function isPendingConnectionRequestStatus(status: string): boolean {
  return status === "pending_auto_accept" || status === "needs_review";
}

export function isConnectionRequestFilter(value: string): value is ConnectionRequestFilter {
  return connectionRequestFilters.includes(value as ConnectionRequestFilter);
}

export function connectionRequestMatchesFilter(
  request: Pick<ConnectionRequestRow, "status">,
  filter: ConnectionRequestFilter,
): boolean {
  return filter === "total" || statusGroup(request.status) === filter;
}

function statusGroup(status: string): RequestStatusGroup {
  if (status === "accepted" || status === "accepted_existing_connection") {
    return "accepted";
  }

  if (status === "pending_auto_accept") {
    return "pending";
  }

  return "needsReview";
}
